'use client';
import React, { Fragment, useState } from 'react';
import { BsCameraFill } from 'react-icons/bs';
import styles from './RatingSummary.module.scss';
import StarsPercent from '@/components/atoms/StarsPercent';
import { RiArrowDropDownLine } from 'react-icons/ri';
import { FaCheck, FaFilterCircleXmark } from 'react-icons/fa6';
import { convertToKFormat } from '@/services/ecommerce/utils/helper';

const RatingSummary = ({ reviewSummary, setSortBy, sortBy, ratingFilter, setRatingFilter }) => {
	const [isOpen, setIsOpen] = useState(false);

	const options = [
		{ name: 'Default', value: '' },
		{ name: 'Recent', value: 'desc' },
		{ name: 'Rating low to high', value: 'rating_low_to_high' },
		{ name: 'Rating high to low', value: 'rating_high_to_low' }
	];

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	const selectOption = (optionValue) => {
		setSortBy(optionValue);
		setIsOpen(false);
	};

	const getSelectedOptionName = () => {
		const selected = options.find((option) => option.value === sortBy);
		return selected ? selected.name : 'Select';
	};

	// Handle button click
	const handleFilterRatingClick = (value) => {
		setRatingFilter(value);
	};

	return (
		<div className={styles.product_review_top}>
			<h2 className={styles.title}>Rating & Reviews</h2>
			<div className='d-flex mt-40 mb-24 justify-left'>
				<div className={styles.overall}>
					<div className='d-flex'>
						<div className={styles.rating}>{convertToKFormat(reviewSummary.rs_average || 0)}/5</div>
						{reviewSummary.rs_average !== 0 && (
							<button className={styles.status}>
								★{' '}
								{reviewSummary.rs_average < 1
									? 'Very Poor'
									: reviewSummary.rs_average < 2
										? 'Poor'
										: reviewSummary.rs_average < 3
											? 'Neutral'
											: reviewSummary.rs_average < 4
												? 'Satisfactory'
												: 'Delightful'}
							</button>
						)}
					</div>

					<div className={styles.overallRating}>
						<StarsPercent rating={reviewSummary.rs_average} />
					</div>
					<p className={styles.count}>{convertToKFormat(reviewSummary.rs_total || 0)} Ratings</p>
				</div>

				<div className={styles.breakdown}>
					<div className={styles.breakdown_row}>
						<div className={styles.star_rating}>
							<StarsPercent rating={5} />
						</div>
						<div className={styles.progress_bar}>
							<div
								style={{
									width: `${reviewSummary.rs_five_star ? (reviewSummary.rs_five_star / reviewSummary.rs_total) * 100 : 0}%`,
									maxWidth: `${reviewSummary.rs_five_star ? (reviewSummary.rs_five_star / reviewSummary.rs_total) * 100 : 0}%`
								}}
								className={styles.progress_fill}></div>
						</div>
						<p className={styles.count}>{convertToKFormat(reviewSummary.rs_five_star || 0)}</p>
					</div>

					<div className={styles.breakdown_row}>
						<div className={styles.star_rating}>
							<StarsPercent rating={4} />
						</div>
						<div className={styles.progress_bar}>
							<div
								style={{
									width: `${reviewSummary.rs_four_star ? (reviewSummary.rs_four_star / reviewSummary.rs_total) * 100 : 0}%`,
									maxWidth: `${reviewSummary.rs_four_star ? (reviewSummary.rs_four_star / reviewSummary.rs_total) * 100 : 0}%`
								}}
								className={styles.progress_fill}></div>
						</div>
						<p className={styles.count}>{convertToKFormat(reviewSummary.rs_four_star || 0)}</p>
					</div>

					<div className={styles.breakdown_row}>
						<div className={styles.star_rating}>
							<StarsPercent rating={3} />
						</div>
						<div className={styles.progress_bar}>
							<div
								style={{
									width: `${reviewSummary.rs_three_star ? (reviewSummary.rs_three_star / reviewSummary.rs_total) * 100 : 0}%`,
									maxWidth: `${reviewSummary.rs_three_star ? (reviewSummary.rs_three_star / reviewSummary.rs_total) * 100 : 0}%`
								}}
								className={styles.progress_fill}></div>
						</div>
						<p className={styles.count}>{convertToKFormat(reviewSummary.rs_three_star || 0)}</p>
					</div>

					<div className={styles.breakdown_row}>
						<div className={styles.star_rating}>
							<StarsPercent rating={2} />
						</div>
						<div className={styles.progress_bar}>
							<div
								style={{
									width: `${reviewSummary.rs_two_star ? (reviewSummary.rs_two_star / reviewSummary.rs_total) * 100 : 0}%`,
									maxWidth: `${reviewSummary.rs_two_star ? (reviewSummary.rs_two_star / reviewSummary.rs_total) * 100 : 0}%`
								}}
								className={styles.progress_fill}></div>
						</div>
						<p className={styles.count}>{convertToKFormat(reviewSummary.rs_two_star || 0)}</p>
					</div>

					<div className={styles.breakdown_row}>
						<div className={styles.star_rating}>
							<StarsPercent rating={1} />
						</div>
						<div className={styles.progress_bar}>
							<div
								style={{
									width: `${reviewSummary.rs_one_star ? (reviewSummary.rs_one_star / reviewSummary.rs_total) * 100 : 0}%`,
									maxWidth: `${reviewSummary.rs_one_star ? (reviewSummary.rs_one_star / reviewSummary.rs_total) * 100 : 0}%`
								}}
								className={styles.progress_fill}></div>
						</div>
						<p className={styles.count}>{convertToKFormat(reviewSummary.rs_one_star || 0)}</p>
					</div>
				</div>
			</div>

			<div className={styles.filters}>
				<div className={styles.filter_buttons_blocks}>
					<div
						onClick={() => handleFilterRatingClick('clear')}
						className={`${styles.filter_button} ${ratingFilter === 'clear' ? styles.active : ''}`}>
						<FaFilterCircleXmark className={styles.photo_icon} />
						<span>Clear</span>
					</div>
					<div
						onClick={() => handleFilterRatingClick('hasMedia')}
						className={`${styles.filter_button} ${ratingFilter === 'hasMedia' ? styles.active : ''}`}>
						<BsCameraFill className={styles.photo_icon} />
						<span>Photos</span>
					</div>
					{[5, 4, 3, 2, 1].map((star, index) => (
						<Fragment key={index}>
							<button
								onClick={() => handleFilterRatingClick(star)}
								className={`${styles.filter_button} ${ratingFilter === star ? styles.active : ''}`}>
								<span className={styles.button_start}>★</span>
								{star}
							</button>
						</Fragment>
					))}
				</div>

				<div className={styles.sort_dropdown}>
					<label className={styles.sort_label}>Sort By:</label>
					<div className={styles.dropdown}>
						<button className={styles.dropdown_toggle} onClick={toggleDropdown}>
							{getSelectedOptionName()}
							<RiArrowDropDownLine className={styles.dropdown_arrow} />
						</button>
						{isOpen && (
							<ul className={styles.dropdown_menu}>
								{options.map((option) => (
									<li
										key={option.value}
										className={`${styles.dropdown_item} ${sortBy === option.value ? styles.selected : ''}`}
										onClick={() => selectOption(option.value)}>
										{option.name}
										{sortBy === option.value && <FaCheck className={styles.checkmark} />}
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default RatingSummary;
